import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Add } from '@mui/icons-material'
import { Button, Card, DeleteIcon, EditIcon, Table } from 'components'
import { Box, Switch, useMediaQuery, useTheme } from '@mui/material'
import { TableActionConfig, TableHeaderType } from 'components/Table/types'
import { usePageHeader, usePagination } from 'utils/hooks'
import { successToast } from 'utils/toastService'
import { deviceSNTableColumns } from './deviceSNColumns'
import useAddEditDeviceSNModal from './dialogs/useAddEditDeviceSNModal'
import { useInstrumentsService } from 'services/instrumentsService'
import { TInstrument, TInstrumentWithId } from 'types/models'
import { useDeleteModal } from '../../../../components/dialogs'
import { useRef } from 'react'

export const DeviceSN = () => {
  const queryClient = useQueryClient()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))

  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination({ tableKey: 'device-sn' })

  const {
    getInstruments,
    createInstrument,
    updateInstrument,
    activateInstrument,
    deactivateInstrument,
    deleteInstrument,
  } = useInstrumentsService()

  const { data: instrumentsData, isLoading } = useQuery({
    queryKey: ['instruments_list', currentPage, rowsPerPage],
    queryFn: () =>
      getInstruments({
        currentPage,
        itemsPerPage: rowsPerPage,
      }),
    retry: false,
  })

  const createInstrumentMutation = useMutation(createInstrument, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instruments_list'])
      successToast('Device created')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to create instrument.')
      console.error('Failed to create instrument:', err)
    },
  })

  const updateInstrumentMutation = useMutation(updateInstrument, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instruments_list'])
      successToast('Device updated')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to update instrument.')
      console.error('Failed to update instrument:', err)
    },
  })

  const activateInstrumentMutation = useMutation(activateInstrument, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instruments_list'])
      successToast('Device activated')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to activate instrument.')
      console.error('Failed to activate instrument:', err)
    },
  })

  const deactivateInstrumentMutation = useMutation(deactivateInstrument, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instruments_list'])
      successToast('Device deactivated')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to deactivate instrument.')
      console.error('Failed to deactivate instrument:', err)
    },
  })

  const deleteInstrumentMutation = useMutation(deleteInstrument, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instruments_list'])
      successToast('Device deleted')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to delete instrument.')
      console.error('Failed to delete instrument:', err)
    },
  })

  const [addEditModal, toggleAddEditModal] = useAddEditDeviceSNModal({
    onConfirm: async (newData) => {
      if ((newData as TInstrumentWithId).id) {
        await updateInstrumentMutation.mutateAsync(newData as TInstrumentWithId)
      } else {
        await createInstrumentMutation.mutateAsync(newData as TInstrument)
      }
    },
  })

  const deviceIdRef = useRef<number | null>(null)

  const [deleteModal, toggleDeleteModal] = useDeleteModal({
    children: 'Are you sure you want to delete this device?',
    onConfirm: () => {
      if (deviceIdRef.current !== null) {
        deleteInstrumentMutation.mutate(deviceIdRef.current)
      }
    },
  })

  usePageHeader({
    headerTitle: 'Device SN',
    actionButton: (
      <Button startIcon={<Add />} onClick={() => toggleAddEditModal()}>
        Add Device SN
      </Button>
    ),
  })

  const onPaginationChange = (page: number, perPage: number) => {
    setCurrentPage(page)
    setRowsPerPage(perPage)
  }

  const actions: TableActionConfig<TInstrumentWithId>[] = [
    {
      name: 'activate',
      format: (row: TInstrumentWithId) => (
        <Switch
          size="small"
          checked={row.is_active}
          onChange={() => {
            if (row.is_active) {
              deactivateInstrumentMutation.mutate(row.id)
            } else {
              activateInstrumentMutation.mutate(row.id)
            }
          }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4caf50',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#4caf50',
            },
          }}
        />
      ),
      handler: () => {},
      icon: <></>,
    },
    {
      name: 'edit',
      icon: <EditIcon size={16} />,
      handler: (row) => {
        toggleAddEditModal(row)
      },
    },
    {
      name: 'delete',
      icon: <DeleteIcon size={16} />,
      handler: (row) => {
        deviceIdRef.current = row.id
        toggleDeleteModal()
      },
    },
  ]

  return (
    <Box>
      <Card>
        <Box
          sx={{
            height: isSmallScreen ? 'unset' : 'calc(100vh - 120px)',
          }}
        >
          <Table
            data={instrumentsData?.results || []}
            columns={deviceSNTableColumns({
              actions,
            })}
            headerType={TableHeaderType.Card}
            showPagination={true}
            onPaginationChange={onPaginationChange}
            total={instrumentsData?.pagination?.total || 0}
            tableKey="device-sn"
            isLoading={isLoading}
          />
        </Box>
      </Card>
      {addEditModal}
      {deleteModal}
    </Box>
  )
}
